// StatisticReport2.tsx
import React, { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, type GridRenderCellParams, type GridRowIdGetter } from '@mui/x-data-grid';
import { useFetch } from '../../../hooks/useFetch2.ts';
import { UniversalPivot } from './components/PivotComp.tsx';
import { PivotConfig } from './components/pivotConfig.ts';
import { UniversalPivotWithTotals } from './components/UniversalPivotWithTotals.tsx';
import { FlatEventSummary, reportApi } from './api/api.ts';

// Исходные данные (mock)
const salesData = [
  { shop: 'Лондон', date: '2026-01', category: 'Обувь', sales: 100, qty: 2 },
  { shop: 'Лондон', date: '2026-01', category: 'Одежда', sales: 150, qty: 3 },
  { shop: 'Цюрих',  date: '2026-01', category: 'Обувь', sales: 200, qty: 4 },
  { shop: 'Лондон', date: '2026-02', category: 'Обувь', sales: 120, qty: 1 },
  { shop: 'Цюрих',  date: '2026-02', category: 'Обувь', sales: 50, qty: 1 },
];

// 1. Кейс: Сводная по выручке (Сумма)
const revenueConfig: PivotConfig = {
  yFields: ['shop', 'category'], // Строки: Магазин -> Категория
  xFields: ['date'],             // Колонки: Даты
  bodyExpr: (rows) => rows.reduce((sum, r) => sum + r.sales, 0) // Выражение агрегации
};

// 2. Кейс: Средняя цена за единицу товара (Сложное выражение)
const avgPriceConfig: PivotConfig = {
  yFields: ['category'],
  xFields: ['shop', 'date'],
  bodyExpr: (rows) => {
    const totalSales = rows.reduce((sum, r) => sum + r.sales, 0);
    const totalQty = rows.reduce((sum, r) => sum + r.qty, 0);
    return totalQty > 0 ? (totalSales / totalQty).toFixed(2) : 0;
  }
};

const occupationConfig: PivotConfig = {
  yFields: ['lpr_entity_name'],
  xFields: ['lpr_subtenant_name'],
  bodyExpr: (rows: any[]) => {
    // временно, нужна типизация
    const typedRows = rows as FlatEventSummary[];
    // Считаем через typedRows для проверки типов
    const total = typedRows.reduce((sum, r) => sum + r.cnt_enter - r.cnt_exit + r.difffact, 0);
    return total;
  }
};

// debug
const columns: GridColDef<FlatEventSummary>[] = [
  { field: 'id', headerName: 'ID', width: 90 },
  { field: 'lpr_group_name', headerName: 'Группа', width: 180, valueFormatter: (value) => value ?? '—' },
  { field: 'lpr_subtenant_name', headerName: 'Субарендатор', width: 180, valueFormatter: (value) => value ?? '—' },
  { field: 'lpr_entity_name', headerName: 'Объект', width: 180, valueFormatter: (value) => value ?? '—' },
  { field: 'cnt_enter', headerName: 'Вход', type: 'number', width: 120 },
  { field: 'cnt_exit', headerName: 'Выход', type: 'number', width: 120 },
  { field: 'difffact', headerName: 'Разница (факт)', type: 'number', width: 140 },
];

export const OccupationReport: React.FC = () => {
    const { data, setData, error } = useFetch<FlatEventSummary[]>(reportApi.getEventLastSummary, [], );
    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {/* Отчет по наличию авто на площадках */}
            </Typography>
            <UniversalPivotWithTotals data={data} config={occupationConfig} />
            <Typography variant="h6" sx={{ mt: 12, mb: 2, fontWeight: 'bold' }}>
                Data from backend
            </Typography>
            <DataGrid
                getRowId={(row: FlatEventSummary) => 
                    `${row.lpr_subtenant_name ?? 'no-subtenant'}/${row.lpr_entity_name ?? 'no-entity'}`
                }
                rows={data || []}
                columns={columns}
                pageSizeOptions={[5, 10, 25]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                disableRowSelectionOnClick
            />
        </Box>
    )
}

const avgPriceConfig2: PivotConfig = {
  yFields: ['category'],
  xFields: ['date'],
  bodyExpr: (rows) => {
    const totalSales = rows.reduce((sum, r) => sum + r.sales, 0);
    const totalQty = rows.reduce((sum, r) => sum + r.qty, 0);
    return totalQty > 0 ? (totalSales / totalQty).toFixed(2) : 0;
  }
};

export const OccupationReport2: React.FC = () => {
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 4 }}>
      
      {/* Первый отчет */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Отчет по выручке (Выручка по Магазинам и Категориям)
        </Typography>
        <UniversalPivot data={salesData} config={revenueConfig} />
      </Box>

      {/* Второй отчет */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Анализ средней цены (Категории на пересечении Магазин / Дата)
        </Typography>
        <UniversalPivot data={salesData} config={avgPriceConfig} />
      </Box>

    </Box>
  );
};

export const OccupationReport3: React.FC = () => {
    return (
        <Box>
            <UniversalPivotWithTotals data={salesData} config={revenueConfig} />
            <br/>
            <UniversalPivot data={salesData} config={avgPriceConfig} />
            <br/>
            <UniversalPivot data={salesData} config={avgPriceConfig2} />
        </Box>
    )
}

/*


interface EventCount {
    group_id: number;
    entity_id: number;
    cnt: number;
}

interface Data {
    res_group?: Group[],
    res_entity?: EntityPath[],
    res_event?: EventCount[],
    getSubtenantNameByGroupId: (group_id: number) => string;
}

const defaultData: Data = {
    res_group: [],
    res_entity: [],
    res_event: [],
    getSubtenantNameByGroupId: (group_id: number) => ''
};

interface SubTenantToGrop {
    customer_id: number;
    subtenant_id: number;
    group_id: number;
}

const getReport = async (params: { from: string; to: string }) => {
    const { data: res_subtenant } = await axios.get<Subtenant[]>(`/lpr_subtenant`);
    const { data: res_stg_temp } = await axios.get<SubTenantToGrop[]>(`/lpr_subtenant_to_group`);
    const { data: res_group } = await axios.get<Group[]>(`/lpr_group`);
    const { data: res_entity } = await axios.get<EntityPath[]>(`/v_entity_path`);
    const { data: res_event } = await axios.post<EventCount[]>(`/rpc/sf_get_event_stats`, {
        from_date: params.from,
        to_date: params.to
    });
    
    const subtenantMap = new Map<number, string>(
        res_subtenant.map(s => [s.subtenant_id, s.name])
    );

    const groupToSubtenantMap = new Map<number, number>(
        res_stg_temp.map(stg => [stg.group_id, stg.subtenant_id])
    );

    const getSubtenantNameByGroupId = (groupId: number): string => {
        const subtenantId = groupToSubtenantMap.get(groupId);
        if (!subtenantId) return '';
        
        return subtenantMap.get(subtenantId) || '';
    };    
    return { getSubtenantNameByGroupId, res_group, res_entity, res_event };
}

const calculateSum = (res_entity: EntityPath[], res_group: Group[], idx: number, idx1: number) => {
    res_group.forEach(group => {
        const col = String(group.group_id);
        res_entity[idx][col] = (res_entity[idx][col] || 0) + (res_entity[idx1][col] || 0);
    });
}

// recursive
const calculateTotal = (res_entity: EntityPath[], res_group: Group[], idx: number) => {
    if (res_entity.length == 0) return;

    if (res_entity[idx].type == 3) return; // уже посчитано

    for (let idx1 = 0; idx1 < res_entity.length; idx1++) {
        const entity = res_entity[idx1];
        
        if (entity.parent_id == res_entity[idx].entity_id) {
            calculateTotal(res_entity, res_group, idx1);
            calculateSum(res_entity, res_group, idx, idx1);
        }
    }
}

const addPrefix = (res_entity: EntityPath[]) => {
    const prefix = (type: number) => ''.padEnd(type * 4, ' ');;
    for (let idx1 = 0; idx1 < res_entity.length; idx1++) {
        res_entity[idx1].name = prefix(res_entity[idx1].type) + res_entity[idx1].name;
    }
}

// pivot
const transformToRows = (
    res_group: Group[],
    res_entity: EntityPath[],  
    res_event: EventCount[]
) => {

    res_entity = res_entity.sort((a, b) => a.path_names.localeCompare(b.path_names));
    
    // 1. Создаем быстрый индекс для поиска событий: "entityId_groupId" -> cnt
    const eventLookup = res_event.reduce((acc, event) => {
        const key = `${event.entity_id}_${event.group_id}`;
        acc[key] = event.cnt;
        return acc;
    }, {} as Record<string, number>);

    // 2. Превращаем каждую сущность в строку таблицы
    const res1 = res_entity.map((entity) => {
        // Базовые поля строки
        const row: any = {
            id: entity.entity_id,     // Обязательный ID для DataGrid
            name: entity.name,        // Поле для колонки 'name'
            type: entity.type,        // Поле для колонки 'type'
            entity_id: entity.entity_id,
            parent_id: entity.parent_id,
        };

        // 3. Динамически добавляем колонки групп (реализация findevent)
        res_group.forEach((group) => {
            const fieldName = String(group.group_id);
            const lookupKey = `${entity.entity_id}_${group.group_id}`;
            
            // Если событие найдено в индексе — ставим значение, иначе 0
            row[fieldName] = eventLookup[lookupKey] || 0;
        });

        return row;
    });
    const root = res1.findIndex((entity) => entity.type == 0);
    if (root < 0) return [];

    calculateTotal(res1, res_group, root);
    addPrefix(res1);
    return res1;
};

export const OccupationReport_: React.FC = () => {
    const { t } = useTranslation();

    const [from, setFrom] = useState<Dayjs | null>(dayjs().startOf("day"));
    const [to, setTo] = useState<Dayjs | null>(dayjs().endOf("day"));

    // const { data, setData, error, loading, update } = useFetch<Data>(getReport, {});
    const { data, setData, error, loading, update } = useFetch<Data>(
        getReport,
        defaultData, 
        { from: from?.toISOString(), to: to?.toISOString() } // initialPayload
    );
    const { res_group, res_entity, res_event } = data || {};

    const columns0: GridColDef[] = [
        { field: "name", headerName: t('StatisticReport.entity_name'), type: "string", width: 150 },
        // { field: "type", headerName: 'entity_type', type: "number", flex: 2, width: 100, },
        // { field: "entity_id", headerName: 'entity_id', type: "number", flex: 2, width: 100, },
    ];

    const res_group_sorted = res_group?.sort((a: Group, b: Group): number => {
        const nameA = data.getSubtenantNameByGroupId(a.group_id) || '';
        const nameB = data.getSubtenantNameByGroupId(b.group_id) || '';
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
    });

    const columns1: GridColDef[] = res_group_sorted?.map((group) => ({
        field: String(group.group_id),
        headerName: group.name,
        type: "number",
        // flex: 2,
        width: 100,
        renderHeader: () => (
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2rem', padding: '4px 0' }}>
                
                <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: 'normal' }}>
                    {data.getSubtenantNameByGroupId(group.group_id) || '--'}
                </span>
                
                <span style={{ fontWeight: 'bold' }}>
                    {group.name}
                </span>
            </div>
        ),
    })) || [];
    const columns = [...columns0, ...columns1];

    const rows = useMemo(() => transformToRows(
        data.res_group || [],
        data.res_entity || [],
        data.res_event || []
    ), [data.res_group,
        data.res_entity, 
        data.res_event
    ]);

    const handleRefresh = () => {
        // setData([]);
        update({ from: from?.toISOString(), to: to?.toISOString() });
    };

    return (

    <Paper sx={{ p: 2, maxWidth: 1600, mx: "auto", mt: 4 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="body1">
                {t('StatisticReport.Event statistics_for_the_last_10_days')}
            </Typography>
        </Box>

        <Stack 
            direction="row"
            spacing={2} 
            alignItems="center"
            mb={3}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 20 }}>{t('EventList.from')}</Typography>
                <LocalizedDatePicker
                    value={from}
                    onChange={(v) => setFrom(v)}
                />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 20 }}>{t('EventList.to')}</Typography>
                <LocalizedDatePicker
                    value={to}
                    onChange={(v) => setTo(v)}
                />
            </Stack>
                <Button
                    // fullWidth={false}
                    variant="contained"
                    onClick={handleRefresh}
                    // sx={{ flexGrow: 1 }}
                >
                    {t('EventList.refresh')}
                </Button>            
        </Stack>

        
        <LocalizedGrid
            lang={i18n.language as any}
            gridId='StatisticReport2'
            rows={rows}
            columns={columns} 
            getRowId={(row) => row.entity_id}
            loading={loading}
            disableColumnMenu={true}
            disableColumnSorting={true}
            sx={{ maxWidth: '1600px',
                '& .MuiDataGrid-cell': {
                    whiteSpace: 'pre', // Сохраняет пробелы и не переносит текст
                    // или 'pre-wrap', если вы хотите, чтобы текст переносился при достижении края
                },
            // sx={{
                '& .MuiTablePagination-actions': {
                display: 'none',
                },
                '& .MuiTablePagination-displayedRows': {
                display: 'none',
                },
                '& .MuiTablePagination-selectLabel': {
                display: 'none',
                },
                '& .MuiTablePagination-input': {
                display: 'none',
                },
                // Опционально: если нужно убрать лишние отступы, которые останутся
                '& .MuiTablePagination-spacer': {
                display: 'none',
                },
                '& .MuiTablePagination-toolbar': {
                justifyContent: 'flex-end',
                },
            }}
            hideToolbar={true}
        />

        <Typography color='error'>{error?.message}</Typography>
      
    </Paper>
      
    );

}    
*/